import { SectionType, Section } from "pages/application/applicationService";
import "./sectionBar.scss";
import { faCircle as faCircleRegular } from "@fortawesome/pro-regular-svg-icons";
import useDesktopChecker from "hooks/useDesktopChecker";
import { useState } from "react";
import { SectionBarDropdown } from "./SectionBarDropdown";
import { SectionBarItem } from "./SectionBarItem";

export enum SectionProgressState {
  Completed,
  InProgress,
  NotStarted,
}

interface SectionIndex {
  code: SectionType;
  descriptionText?: string;
  displayName: string;
}

export const sectionInfo: SectionIndex[] = [
  {
    code: "Profile",
    descriptionText: "Borrower Information",
    displayName: "Profile",
  },
  {
    code: "Property",
    descriptionText: "Property Details",
    displayName: "Property",
  },
  {
    code: "Credit",
    descriptionText: "DOB & SSN",
    displayName: "Credit",
  },
  {
    code: "Finances",
    descriptionText: "Income & Assets",
    displayName: "Finances",
  },
  {
    code: "Acknowledgements",
    descriptionText: "Government Information",
    displayName: "Declarations",
  },
];

export default function SectionBar({ activeSection, sections }: { readonly activeSection: SectionType; readonly sections: Section[] }) {
  if (activeSection === "Unknown") {
    throw new Error(`Section not found`);
  }
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const isDesktop = useDesktopChecker();

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  return (
    <nav className="section-bar">
      <div className={`section-bar__wrapper-container ${!isDesktop && isDropdownOpen ? "section-bar__wrapper-container--fullscreen" : ""}`}>
        {!isDesktop && (
          <SectionBarDropdown
            activeSection={activeSection}
            isDropdownOpen={isDropdownOpen}
            onToggle={toggleDropdown}
            getSectionIndex={getSectionIndex}
            getSectionDisplayName={getSectionDisplayName}
            getSectionDescription={getSectionDescription}
            sections={sections}
            getSectionState={getSectionState}
          />
        )}
        {(isDesktop || isDropdownOpen) && (
          <ul className="section-bar__wrapper">
            {sections.map((section) => {
              return (
                <SectionBarItem
                  key={section.sectionCode}
                  name={getSectionDisplayName(section.sectionCode)}
                  icon={faCircleRegular}
                  state={getSectionState(activeSection, section)}
                  sectionDescription={getSectionDescription(section.sectionCode)}
                  isActive={section.sectionCode === activeSection}
                  index={sections.indexOf(section) + 1}
                />
              );
            })}
          </ul>
        )}
      </div>
    </nav>
  );
}

const getSectionDescription = (sectionCode: string) => {
  const section = sectionInfo.find((s) => s.code === sectionCode);
  if (section) {
    return section.descriptionText;
  } else {
    throw new Error(`Section not found`);
  }
};

const getSectionDisplayName = (sectionCode: string) => {
  const section = sectionInfo.find((s) => s.code === sectionCode);
  if (section) {
    return section.displayName;
  } else {
    throw new Error(`Section not found`);
  }
};

const getSectionIndex = (sectionCode: string) => {
  const section = sectionInfo.find((s) => s.code === sectionCode);
  if (section) {
    return sectionInfo.indexOf(section) + 1;
  } else {
    throw new Error(`Section not found`);
  }
};

const getSectionState = (activeSection: string, section: Section) => {
  if (section.complete) {
    return SectionProgressState.Completed;
  } else {
    return activeSection === section.sectionCode ? SectionProgressState.InProgress : SectionProgressState.NotStarted;
  }
};
